import { useState, useCallback, useEffect } from 'react';

interface FilterState {
  search?: string;
  [key: string]: any;
}

interface UseFilterStateOptions {
  initialFilters?: FilterState;
  persistKey?: string; // Key for localStorage persistence
  onFilterChange?: (filters: FilterState) => void;
}

export function useFilterState({
  initialFilters = {},
  persistKey,
  onFilterChange,
}: UseFilterStateOptions = {}) {
  // Load initial state from localStorage if persistKey is provided
  const getInitialState = useCallback((): FilterState => {
    if (persistKey) {
      try {
        const saved = localStorage.getItem(`mtls-filters-${persistKey}`);
        if (saved) {
          return { ...initialFilters, ...JSON.parse(saved) };
        }
      } catch (error) {
        console.warn('Failed to load saved filters:', error);
      }
    }
    return initialFilters;
  }, [initialFilters, persistKey]);

  const [filters, setFilters] = useState<FilterState>(getInitialState);

  // Save to localStorage when filters change
  useEffect(() => {
    if (persistKey) {
      try {
        localStorage.setItem(`mtls-filters-${persistKey}`, JSON.stringify(filters));
      } catch (error) {
        console.warn('Failed to save filters:', error);
      }
    }
    
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters, persistKey, onFilterChange]);

  const updateFilter = useCallback((key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const updateFilters = useCallback((newFilters: Partial<FilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const clearFilter = useCallback((key: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  const hasActiveFilters = useCallback(() => {
    return Object.values(filters).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v !== undefined && v !== '');
      }
      return value !== undefined && value !== '' && value !== null;
    });
  }, [filters]);

  const getActiveFilterCount = useCallback(() => {
    return Object.values(filters).filter(value => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v !== undefined && v !== '');
      }
      return value !== undefined && value !== '' && value !== null;
    }).length;
  }, [filters]);

  // Build query parameters for API calls
  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.append(key, value.join(','));
          }
        } else if (typeof value === 'object') {
          // Handle date ranges and other objects
          Object.entries(value).forEach(([subKey, subValue]) => {
            if (subValue !== undefined && subValue !== '') {
              params.append(`${key}.${subKey}`, String(subValue));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });
    
    return params;
  }, [filters]);

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    clearFilter,
    hasActiveFilters: hasActiveFilters(),
    activeFilterCount: getActiveFilterCount(),
    getQueryParams,
  };
}